import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    Grid,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    Popover,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GlobalContext } from 'GlobalContext';
import { EmployeeService } from 'services/EmployeeService';

const employeeService = new EmployeeService();
const INDEFINITE_DATE = dayjs('3000-01-01');
const TIMELINE_COLORS = ['#1976d2', '#388e3c', '#f57c00', '#d32f2f', '#7b1fa2', '#0097a7', '#e64a19', '#558b2f'];

const toDayjs = (value) => {
    if (!value) {
        return dayjs();
    }

    if (dayjs.isDayjs(value)) {
        return value;
    }

    return dayjs(value);
};

const buildEmptyRow = (employeeId, localId, baseStartDate = dayjs()) => {
    const startDate = toDayjs(baseStartDate);

    return {
        local_id: localId,
        hiring_range_id: null,
        hiring_id: null,
        employee_id: employeeId,
        start_date: startDate,
        end_date: startDate.add(30, 'day'),
        name_hiring: '',
        type_hiring: '',
        position: '',
        enable: false,
        automatic_renewal: false,
    };
};

const validateRows = (rows, t) => {
    if (rows.length === 0) {
        return t('timekeeper.employees.dialog.assign_contract.manager.validation.empty');
    }

    for (const row of rows) {
        if (!row.name_hiring || row.name_hiring.trim().length < 5 || row.name_hiring.trim().length > 30) {
            return t('timekeeper.employees.dialog.assign_contract.manager.validation.name');
        }

        const start = toDayjs(row.start_date);
        const end = toDayjs(row.end_date);

        if (!start.isValid() || !end.isValid()) {
            return t('timekeeper.employees.dialog.assign_contract.manager.validation.invalid_dates');
        }

        if (end.isBefore(start, 'day')) {
            return t('timekeeper.employees.dialog.assign_contract.manager.validation.invalid_range');
        }
    }

    const sorted = [...rows].sort((a, b) => toDayjs(a.start_date).valueOf() - toDayjs(b.start_date).valueOf());

    for (let i = 1; i < sorted.length; i += 1) {
        const prev = sorted[i - 1];
        const current = sorted[i];

        if (!toDayjs(current.start_date).isAfter(toDayjs(prev.end_date), 'day')) {
            return t('timekeeper.employees.dialog.assign_contract.manager.validation.overlap');
        }
    }

    return null;
};

const ContractTimeline = ({ rows, onBoundaryClick, onSegmentClick }) => {
    if (!Array.isArray(rows) || rows.length === 0) {
        return null;
    }

    const sorted = [...rows].sort((a, b) => toDayjs(a.start_date).valueOf() - toDayjs(b.start_date).valueOf());
    const firstStart = toDayjs(sorted[0]?.start_date);
    const hasIndefiniteEnd = toDayjs(sorted[sorted.length - 1]?.end_date).isSame(INDEFINITE_DATE, 'day');
    const finiteEnds = sorted.map((row) => toDayjs(row.end_date)).filter((end) => end.isValid() && !end.isSame(INDEFINITE_DATE, 'day'));
    const lastFiniteEnd = finiteEnds.length > 0 ? finiteEnds[finiteEnds.length - 1] : firstStart;
    const indefiniteFallbackEnd = toDayjs(sorted[sorted.length - 1]?.start_date).add(30, 'day');
    const timelineEnd = hasIndefiniteEnd
        ? (lastFiniteEnd.isAfter(indefiniteFallbackEnd, 'day') ? lastFiniteEnd : indefiniteFallbackEnd)
        : toDayjs(sorted[sorted.length - 1]?.end_date);
    const totalDays = Math.max(1, timelineEnd.diff(firstStart, 'day'));

    const toPercent = (dateValue) => {
        const current = toDayjs(dateValue);

        if (!current.isValid()) {
            return 0;
        }

        const dayOffset = current.diff(firstStart, 'day');
        const bounded = Math.max(0, Math.min(totalDays, dayOffset));
        return (bounded / totalDays) * 100;
    };

    const segments = sorted.map((row, index) => {
        const start = toDayjs(row.start_date);
        const rowEnd = toDayjs(row.end_date);
        const end = rowEnd.isSame(INDEFINITE_DATE, 'day') ? timelineEnd : rowEnd;
        const left = toPercent(start);
        const right = toPercent(end);

        return {
            row,
            index,
            left,
            right,
            width: Math.max(0.5, right - left),
            center: left + Math.max(0.5, right - left) / 2,
        };
    });

    const boundaries = Array.from({ length: sorted.length + 1 }, (_, i) => {
        if (i === 0) {
            return { index: i, date: toDayjs(sorted[0]?.start_date), left: toPercent(sorted[0]?.start_date) };
        }

        if (i === sorted.length) {
            return { index: i, date: toDayjs(sorted[sorted.length - 1]?.end_date), left: 100 };
        }

        return { index: i, date: toDayjs(sorted[i]?.start_date), left: toPercent(sorted[i]?.start_date) };
    });

    return (
        <Box sx={{ px: 1.5, py: 1.5, mb: 1.5, bgcolor: 'action.hover', borderRadius: 1.5 }}>
            <Box sx={{ position: 'relative', height: 20, mb: 0.5 }}>
                {segments.map(({ row, index, center, width }) => (
                    <Box
                        key={row.local_id}
                        sx={{
                            position: 'absolute',
                            left: `${center}%`,
                            width: `${Math.max(width, 8)}%`,
                            transform: 'translateX(-50%)',
                            px: 0.5,
                            textAlign: 'center',
                            overflow: 'hidden',
                        }}
                    >
                        <Tooltip title={onSegmentClick ? 'Click para editar contrato' : ''} placement="top">
                            <Typography
                                variant="caption"
                                onClick={() => onSegmentClick?.(row.local_id)}
                                sx={{
                                    color: TIMELINE_COLORS[index % TIMELINE_COLORS.length],
                                    fontWeight: 700,
                                    display: 'block',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    cursor: onSegmentClick ? 'pointer' : 'default',
                                    '&:hover': onSegmentClick ? { textDecoration: 'underline' } : {},
                                }}
                            >
                                {row.name_hiring || '—'}
                            </Typography>
                        </Tooltip>
                    </Box>
                ))}
            </Box>

            <Box sx={{ position: 'relative', height: 18, display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: '100%', height: 10, borderRadius: 1, bgcolor: 'action.disabledBackground' }} />

                <Box sx={{ position: 'absolute', left: 0, width: '100%', height: 10 }}>
                    {segments.map(({ row, index, left, width }) => (
                        <Box
                            key={`segment-${row.local_id}`}
                            sx={{
                                position: 'absolute',
                                left: `${left}%`,
                                width: `${width}%`,
                                height: 10,
                                borderRadius: 1,
                                bgcolor: TIMELINE_COLORS[index % TIMELINE_COLORS.length],
                            }}
                        />
                    ))}
                </Box>

                {boundaries.map(({ index: i, date: dotDate, left }) => {
                    const dotDateLabel = dotDate.isSame(INDEFINITE_DATE, 'day') ? 'Indefinido' : dotDate.format('DD/MM/YYYY');

                    return (
                        <Tooltip key={`dot-tooltip-${i}`} title={onBoundaryClick ? `Click para editar fecha (${dotDateLabel})` : dotDateLabel} placement="top">
                            <Box
                                onClick={(event) => onBoundaryClick?.(i, event.currentTarget)}
                                sx={{
                                    position: 'absolute',
                                    left: `${left}%`,
                                    transform: 'translateX(-50%)',
                                    width: i === 0 || i === sorted.length ? 14 : 10,
                                    height: i === 0 || i === sorted.length ? 14 : 10,
                                    borderRadius: '50%',
                                    bgcolor: i === 0 ? 'primary.dark' : i === sorted.length ? '#bdbdbd' : 'background.paper',
                                    border: '2px solid',
                                    borderColor: i === 0 ? 'primary.main' : i === sorted.length ? '#9e9e9e' : 'text.disabled',
                                    zIndex: 2,
                                    cursor: onBoundaryClick ? 'pointer' : 'default',
                                    '&:hover': onBoundaryClick ? { transform: 'translateX(-50%) scale(1.2)' } : {},
                                }}
                            />
                        </Tooltip>
                    );
                })}
            </Box>

            <Box sx={{ position: 'relative', height: 20, mt: 0.25 }}>
                <Typography
                    variant="caption"
                    sx={{
                        position: 'absolute',
                        left: `${boundaries[0]?.left ?? 0}%`,
                        transform: 'translateX(-50%)',
                        fontSize: '0.6rem',
                        color: 'primary.main',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                    }}
                >
                    {toDayjs(sorted[0]?.start_date).isValid() ? toDayjs(sorted[0]?.start_date).format('DD/MM/YYYY') : ''}
                </Typography>

                {boundaries.slice(1, boundaries.length - 1).map((boundary) => (
                    <Typography
                        key={`start-${boundary.index}`}
                        variant="caption"
                        sx={{
                            position: 'absolute',
                            left: `${boundary.left}%`,
                            transform: 'translateX(-50%)',
                            fontSize: '0.6rem',
                            color: 'text.secondary',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {boundary.date.isValid() ? boundary.date.format('DD/MM/YYYY') : ''}
                    </Typography>
                ))}

                <Typography
                    variant="caption"
                    sx={{
                        position: 'absolute',
                        left: `${boundaries[boundaries.length - 1]?.left ?? 100}%`,
                        transform: 'translateX(-100%)',
                        fontSize: '0.6rem',
                        color: 'text.secondary',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {toDayjs(sorted[sorted.length - 1]?.end_date).isSame(INDEFINITE_DATE, 'day') ? '∞ Indefinido' : toDayjs(sorted[sorted.length - 1]?.end_date).format('DD/MM/YYYY')}
                </Typography>
            </Box>
        </Box>
    );
};

const ContractDetailModal = ({ open, row, onClose, onSave }) => {
    const { t } = useTranslation();

    const [name, setName] = useState('');
    const [startDate, setStartDate] = useState(dayjs());
    const [endDate, setEndDate] = useState(dayjs());
    const [type, setType] = useState('');
    const [position, setPosition] = useState('');
    const [active, setActive] = useState(false);
    const [indefinite, setIndefinite] = useState(false);
    const [renewAutomatic, setRenewAutomatic] = useState(false);

    useEffect(() => {
        if (!open || !row) {
            return;
        }

        const start = toDayjs(row.start_date);
        const end = toDayjs(row.end_date);

        setName(row.name_hiring || '');
        setStartDate(start);
        setEndDate(end);
        setType(row.type_hiring || '');
        setPosition(row.position || '');
        setActive(!!row.enable);
        setRenewAutomatic(!!row.automatic_renewal);
        setIndefinite(end.isSame(INDEFINITE_DATE, 'day'));
    }, [open, row]);

    const nameErrorMessage = useMemo(() => {
        if (!name || name.trim().length < 5 || name.trim().length > 30) {
            return t('timekeeper.employees.dialog.assign_contract.tooltips.min_or_max_name');
        }

        return '';
    }, [name, t]);

    const dateErrorMessage = useMemo(() => {
        if (!startDate?.isValid() || !endDate?.isValid()) {
            return t('timekeeper.employees.dialog.assign_contract.manager.validation.invalid_dates');
        }

        if (!indefinite && endDate.isBefore(startDate, 'day')) {
            return t('timekeeper.employees.dialog.assign_contract.manager.validation.invalid_range');
        }

        return '';
    }, [endDate, indefinite, startDate, t]);

    const typeErrorMessage = useMemo(() => {
        if (!type || type.trim().length < 5 || type.trim().length > 30) {
            return t('timekeeper.employees.dialog.assign_contract.tooltips.min_or_max_type');
        }

        return '';
    }, [t, type]);

    const positionErrorMessage = useMemo(() => {
        if (!position || position.trim().length < 5 || position.trim().length > 30) {
            return t('timekeeper.employees.dialog.assign_contract.tooltips.min_or_max_position');
        }

        return '';
    }, [position, t]);

    const validationMessage = nameErrorMessage || dateErrorMessage || typeErrorMessage || positionErrorMessage;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ background: (theme) => theme.palette.background.default }}>
                <Typography variant="body3">{t('timekeeper.employees.dialog.assign_contract.title')}</Typography>
            </DialogTitle>

            <DialogContent dividers sx={{ background: (theme) => theme.palette.background.default }}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                    <Grid container spacing={2}>
                        <Grid size={12}>
                            <TextField
                                fullWidth
                                required
                                label={t('timekeeper.employees.dialog.assign_contract.name')}
                                value={name}
                                onChange={(event) => setName(event.target.value)}
                                error={!!nameErrorMessage}
                                helperText={nameErrorMessage}
                                slotProps={{
                                    htmlInput: { maxLength: 30 },
                                    input: {
                                        endAdornment: <InputAdornment position="end">{`${name.length}/30`}</InputAdornment>,
                                    },
                                }}
                            />
                        </Grid>

                        <Grid size={6}>
                            <DatePicker
                                label={t('timekeeper.employees.dialog.assign_contract.start_date')}
                                value={startDate}
                                onChange={(value) => {
                                    if (value?.isValid()) {
                                        setStartDate(value);
                                    }
                                }}
                                slotProps={{
                                    textField: {
                                        required: true,
                                        fullWidth: true,
                                        error: !!dateErrorMessage,
                                        helperText: !indefinite ? dateErrorMessage : '',
                                    },
                                }}
                            />
                        </Grid>

                        {!indefinite && (
                            <Grid size={6}>
                                <DatePicker
                                    label={t('timekeeper.employees.dialog.assign_contract.end_date')}
                                    value={endDate}
                                    onChange={(value) => {
                                        if (value?.isValid()) {
                                            setEndDate(value);
                                        }
                                    }}
                                    slotProps={{
                                        textField: {
                                            required: true,
                                            fullWidth: true,
                                            error: !!dateErrorMessage,
                                            helperText: dateErrorMessage,
                                        },
                                    }}
                                />
                            </Grid>
                        )}

                        <Grid size={6}>
                            <TextField
                                fullWidth
                                required
                                label={t('timekeeper.employees.dialog.assign_contract.type')}
                                value={type}
                                onChange={(event) => setType(event.target.value)}
                                error={!!typeErrorMessage}
                                helperText={typeErrorMessage}
                                slotProps={{
                                    htmlInput: { maxLength: 30 },
                                    input: {
                                        endAdornment: <InputAdornment position="end">{`${type.length}/30`}</InputAdornment>,
                                    },
                                }}
                            />
                        </Grid>

                        <Grid size={6}>
                            <TextField
                                fullWidth
                                required
                                label={t('timekeeper.employees.dialog.assign_contract.position')}
                                value={position}
                                onChange={(event) => setPosition(event.target.value)}
                                error={!!positionErrorMessage}
                                helperText={positionErrorMessage}
                                slotProps={{
                                    htmlInput: { maxLength: 30 },
                                    input: {
                                        endAdornment: <InputAdornment position="end">{`${position.length}/30`}</InputAdornment>,
                                    },
                                }}
                            />
                        </Grid>

                        <Grid size={12}>
                            <FormGroup>
                                <FormControlLabel
                                    control={<Checkbox checked={active} onChange={(event) => setActive(event.target.checked)} />}
                                    label={t('timekeeper.employees.dialog.assign_contract.active')}
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={renewAutomatic} onChange={(event) => setRenewAutomatic(event.target.checked)} />}
                                    label={t('timekeeper.employees.dialog.assign_contract.renew_automatic')}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={indefinite}
                                            onChange={(event) => {
                                                const checked = event.target.checked;
                                                setIndefinite(checked);

                                                if (checked) {
                                                    setEndDate(INDEFINITE_DATE);
                                                } else if (toDayjs(endDate).isSame(INDEFINITE_DATE, 'day')) {
                                                    setEndDate(toDayjs(startDate).add(30, 'day'));
                                                }
                                            }}
                                        />
                                    }
                                    label={t('timekeeper.employees.dialog.assign_contract.indefinite')}
                                />
                            </FormGroup>
                        </Grid>
                    </Grid>
                </LocalizationProvider>
            </DialogContent>

            <DialogActions sx={{ background: (theme) => theme.palette.background.default }}>
                <Button variant="contained" disableElevation color="error" onClick={onClose}>
                    {t('timekeeper.employees.dialog.assign_contract.buttons.cancel')}
                </Button>

                <Tooltip title={validationMessage || ''}>
                    <span>
                        <Button
                            variant="contained"
                            disableElevation
                            disabled={!!validationMessage}
                            onClick={() =>
                                onSave({
                                    name_hiring: name.trim(),
                                    type_hiring: type.trim(),
                                    position: position.trim(),
                                    start_date: startDate,
                                    end_date: indefinite ? INDEFINITE_DATE : endDate,
                                    enable: active,
                                    automatic_renewal: renewAutomatic,
                                })
                            }
                        >
                            {t('timekeeper.employees.dialog.assign_contract.buttons.assign')}
                        </Button>
                    </span>
                </Tooltip>
            </DialogActions>
        </Dialog>
    );
};

const AssignContract = ({ open, setOpen, reloading, data }) => {
    const { t } = useTranslation();
    const { handlerAlert } = useContext(GlobalContext);

    const employeeId = data?.employee_id ?? data?.id;

    const [rows, setRows] = useState([]);
    const [deletedHiringIds, setDeletedHiringIds] = useState([]);
    const [deletedHiringRangeIds, setDeletedHiringRangeIds] = useState([]);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [alerts, setAlerts] = useState(false);

    const [editingRowId, setEditingRowId] = useState(null);
    const [timelineEditor, setTimelineEditor] = useState({ boundaryIndex: null, anchorEl: null });

    const localIdCounter = useRef(1);
    const hasUnsavedChangesRef = useRef(false);

    const sortedRows = useMemo(() => [...rows].sort((a, b) => toDayjs(a.start_date).valueOf() - toDayjs(b.start_date).valueOf()), [rows]);
    const editingRow = useMemo(() => sortedRows.find((row) => row.local_id === editingRowId) || null, [editingRowId, sortedRows]);

    const markDirty = useCallback(() => {
        hasUnsavedChangesRef.current = true;
    }, []);

    const loadData = useCallback(async () => {
        if (!open || !employeeId) {
            return;
        }

        setLoading(true);

        try {
            const hiringRanges = await employeeService.employee.getHiringRange({ employee_id: employeeId });

            const mapped = (hiringRanges || []).map((range, index) => {
                const hiring = range.hiring || {};

                return {
                    local_id: index + 1,
                    hiring_range_id: range.hiring_range_id,
                    hiring_id: hiring.hiring_id || null,
                    employee_id: range.employee_id,
                    start_date: toDayjs(range.start_date),
                    end_date: toDayjs(range.end_date),
                    name_hiring: hiring.name_hiring || '',
                    type_hiring: hiring.type_hiring || '',
                    position: hiring.position || '',
                    enable: !!hiring.enable,
                    automatic_renewal: !!hiring.automatic_renewal,
                };
            });

            localIdCounter.current = (mapped.length || 0) + 1;
            setRows(mapped);
            setDeletedHiringIds([]);
            setDeletedHiringRangeIds([]);
            hasUnsavedChangesRef.current = false;
        } catch (error) {
            console.error(error);
            handlerAlert(true, {
                title: t('timekeeper.employees.dialog.assign_contract.messages.error.title'),
                message: t('timekeeper.employees.dialog.assign_contract.manager.alerts.load_error'),
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    }, [employeeId, handlerAlert, open, t]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const closeModal = useCallback(
        (skipConfirm = false) => {
            if (!skipConfirm && hasUnsavedChangesRef.current) {
                setAlerts(true);
                return;
            }

            setOpen(false);
            setRows([]);
            setDeletedHiringIds([]);
            setDeletedHiringRangeIds([]);
            setEditingRowId(null);
            setTimelineEditor({ boundaryIndex: null, anchorEl: null });
            setSaving(false);
            setLoading(false);
            setAlerts(false);
            hasUnsavedChangesRef.current = false;
        },
        [setOpen],
    );

    const updateRow = useCallback(
        (localId, patch) => {
            setRows((previous) => previous.map((row) => (row.local_id === localId ? { ...row, ...patch } : row)));
            markDirty();
        },
        [markDirty],
    );

    const handleTimelineBoundaryClick = useCallback(
        (boundaryIndex, anchorEl) => {
            if (saving || loading) {
                return;
            }

            setTimelineEditor({ boundaryIndex, anchorEl });
        },
        [loading, saving],
    );

    const handleTimelineSegmentClick = useCallback((localId) => {
        setEditingRowId(localId);
    }, []);

    const closeTimelineEditor = useCallback(() => {
        setTimelineEditor({ boundaryIndex: null, anchorEl: null });
    }, []);

    const selectedBoundaryDate = useMemo(() => {
        const boundaryIndex = timelineEditor.boundaryIndex;

        if (boundaryIndex === null || sortedRows.length === 0) {
            return null;
        }

        if (boundaryIndex === 0) {
            return toDayjs(sortedRows[0].start_date);
        }

        if (boundaryIndex === sortedRows.length) {
            return toDayjs(sortedRows[sortedRows.length - 1].end_date);
        }

        return toDayjs(sortedRows[boundaryIndex].start_date);
    }, [sortedRows, timelineEditor.boundaryIndex]);

    const applyTimelineBoundaryDate = useCallback(
        (nextDate) => {
            if (!nextDate?.isValid() || timelineEditor.boundaryIndex === null) {
                return;
            }

            const boundaryIndex = timelineEditor.boundaryIndex;

            setRows((previous) => {
                const sorted = [...previous].sort((a, b) => toDayjs(a.start_date).valueOf() - toDayjs(b.start_date).valueOf());

                if (sorted.length === 0) {
                    return previous;
                }

                const updates = new Map();

                if (boundaryIndex === 0) {
                    updates.set(sorted[0].local_id, { start_date: nextDate });
                } else if (boundaryIndex === sorted.length) {
                    updates.set(sorted[sorted.length - 1].local_id, { end_date: nextDate });
                } else {
                    updates.set(sorted[boundaryIndex - 1].local_id, { end_date: nextDate.subtract(1, 'day') });
                    updates.set(sorted[boundaryIndex].local_id, { start_date: nextDate });
                }

                return previous.map((row) => (updates.has(row.local_id) ? { ...row, ...updates.get(row.local_id) } : row));
            });

            markDirty();
            closeTimelineEditor();
        },
        [closeTimelineEditor, markDirty, timelineEditor.boundaryIndex],
    );

    const handleAddRowAfter = useCallback(
        (row) => {
            const nextStart = toDayjs(row?.end_date).isValid() ? toDayjs(row.end_date).add(1, 'day') : dayjs();
            const nextRow = buildEmptyRow(employeeId, localIdCounter.current, nextStart);

            localIdCounter.current += 1;
            setRows((previous) => [...previous, nextRow]);
            markDirty();
        },
        [employeeId, markDirty],
    );

    const handleDeleteRow = useCallback(
        (row) => {
            setRows((previous) => previous.filter((item) => item.local_id !== row.local_id));

            if (row.hiring_id) {
                setDeletedHiringIds((previous) => [...previous, row.hiring_id]);
            }

            if (row.hiring_range_id) {
                setDeletedHiringRangeIds((previous) => [...previous, row.hiring_range_id]);
            }

            markDirty();
        },
        [markDirty],
    );

    const persistRows = useCallback(async () => {
        const validationError = validateRows(rows, t);

        if (validationError) {
            handlerAlert(true, {
                title: t('timekeeper.employees.dialog.assign_contract.messages.error.title'),
                message: validationError,
                severity: 'error',
            });
            return;
        }

        setSaving(true);

        try {
            for (const hiringId of deletedHiringIds) {
                await employeeService.employee.deleteHiring(hiringId);
            }

            for (const hiringRangeId of deletedHiringRangeIds) {
                await employeeService.employee.deleteHiringRange(hiringRangeId);
            }

            const persistedRows = [...rows].sort((a, b) => toDayjs(a.start_date).valueOf() - toDayjs(b.start_date).valueOf());

            for (const row of persistedRows) {
                const rangePayload = {
                    employee_id: row.employee_id,
                    start_date: toDayjs(row.start_date).format('YYYY-MM-DD'),
                    end_date: toDayjs(row.end_date).format('YYYY-MM-DD'),
                };

                let hiringRangeId = row.hiring_range_id;

                if (hiringRangeId) {
                    await employeeService.employee.updateHiringRange(hiringRangeId, {
                        ...rangePayload,
                        hiring_range_id: hiringRangeId,
                    });
                } else {
                    const createdRange = await employeeService.employee.createHiringRange(rangePayload);
                    hiringRangeId = createdRange.hiring_range_id;
                }

                const hiringPayload = {
                    name_hiring: row.name_hiring.trim(),
                    type_hiring: (row.type_hiring || row.name_hiring || '').trim(),
                    position: (row.position || row.name_hiring || '').trim(),
                    enable: !!row.enable,
                    automatic_renewal: !!row.automatic_renewal,
                };

                if (row.hiring_id) {
                    await employeeService.employee.updateHiring(row.hiring_id, hiringPayload);
                } else {
                    await employeeService.employee.createHiring({
                        hiring_range_id: hiringRangeId,
                        ...hiringPayload,
                    });
                }
            }

            handlerAlert(true, {
                title: t('timekeeper.employees.dialog.assign_contract.messages.success.title'),
                message: t('timekeeper.employees.dialog.assign_contract.manager.alerts.success'),
                severity: 'success',
            });

            reloading();
            closeModal(true);
        } catch (error) {
            console.error(error);
            handlerAlert(true, {
                title: t('timekeeper.employees.dialog.assign_contract.messages.error.title'),
                message: t('timekeeper.employees.dialog.assign_contract.manager.alerts.error'),
                severity: 'error',
            });
        } finally {
            setSaving(false);
        }
    }, [closeModal, deletedHiringIds, deletedHiringRangeIds, handlerAlert, reloading, rows, t]);

    const columns = useMemo(
        () => [
            {
                field: 'actions',
                headerName: t('timekeeper.employees.dialog.assign_contract.manager.actions_label'),
                type: 'actions',
                width: 150,
                getActions: (params) => [
                    <GridActionsCellItem key="delete" icon={<DeleteIcon color="error" />} label={t('general.buttons.delete')} onClick={() => handleDeleteRow(params.row)} showInMenu={false} disabled={saving || loading} />,
                    <GridActionsCellItem key="add" icon={<AddIcon />} label={t('general.buttons.add')} onClick={() => handleAddRowAfter(params.row)} showInMenu={false} disabled={saving || loading} />,
                    <GridActionsCellItem
                        key="edit"
                        icon={<EditIcon color="primary" />}
                        label={t('timekeeper.employees.dialog.assign_contract.manager.edit_detail')}
                        onClick={() => setEditingRowId(params.row.local_id)}
                        showInMenu={false}
                        disabled={saving || loading}
                    />,
                ],
                sortable: false,
                filterable: false,
            },
            {
                field: 'name_hiring',
                headerName: t('timekeeper.employees.dialog.assign_contract.name'),
                flex: 1.4,
                sortable: false,
                filterable: false,
                renderCell: (params) => (
                    <Box sx={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center' }}>
                        <TextField
                            value={params.row.name_hiring || ''}
                            onChange={(event) => {
                                const nextName = event.target.value;
                                updateRow(params.row.local_id, {
                                    name_hiring: nextName,
                                    type_hiring: nextName,
                                    position: nextName,
                                });
                            }}
                            disabled={saving || loading}
                            size="small"
                            fullWidth
                            inputProps={{ maxLength: 30 }}
                        />
                    </Box>
                ),
            },
            {
                field: 'start_date',
                headerName: t('timekeeper.employees.dialog.assign_contract.start_date'),
                flex: 1,
                sortable: false,
                filterable: false,
                renderCell: (params) => (
                    <Box sx={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center' }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                            <DatePicker
                                value={toDayjs(params.row.start_date)}
                                onChange={(value) => {
                                    if (value?.isValid()) {
                                        updateRow(params.row.local_id, { start_date: value });
                                    }
                                }}
                                disabled={saving || loading}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        fullWidth: true,
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Box>
                ),
            },
            {
                field: 'end_date',
                headerName: t('timekeeper.employees.dialog.assign_contract.end_date'),
                flex: 1,
                sortable: false,
                filterable: false,
                renderCell: (params) => {
                    const isIndefinite = toDayjs(params.row.end_date).isSame(INDEFINITE_DATE, 'day');

                    if (isIndefinite) {
                        return (
                            <Box sx={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    Indefinido
                                </Typography>
                            </Box>
                        );
                    }

                    return (
                        <Box sx={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center' }}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                                <DatePicker
                                    value={toDayjs(params.row.end_date)}
                                    onChange={(value) => {
                                        if (value?.isValid()) {
                                            updateRow(params.row.local_id, { end_date: value });
                                        }
                                    }}
                                    disabled={saving || loading}
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            fullWidth: true,
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Box>
                    );
                },
            },
        ],
        [handleAddRowAfter, handleDeleteRow, loading, saving, t, updateRow],
    );

    const managerValidation = useMemo(() => validateRows(rows, t), [rows, t]);

    const tooltipContent = useMemo(() => {
        if (!managerValidation) {
            return '';
        }

        return (
            <List dense>
                <ListItem>
                    <ListItemText primary={managerValidation} />
                </ListItem>
            </List>
        );
    }, [managerValidation]);

    return (
        <>
            <Dialog open={open} onClose={() => closeModal(false)} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ background: (theme) => theme.palette.background.default }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                        <Typography variant="body3">{t('timekeeper.employees.dialog.assign_contract.manager.title')}</Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                                const next = buildEmptyRow(employeeId, localIdCounter.current);
                                localIdCounter.current += 1;
                                setRows((previous) => [...previous, next]);
                                markDirty();
                            }}
                            disabled={saving || loading}
                        >
                            {t('timekeeper.employees.dialog.assign_contract.manager.add_row')}
                        </Button>
                    </Stack>
                </DialogTitle>

                {alerts && (
                    <Box sx={{ top: 0, zIndex: 10, pb: 1, px: 3, pt: 2 }}>
                        <Alert
                            variant="filled"
                            severity="warning"
                            action={
                                <>
                                    <Button onClick={() => setAlerts(false)} color="inherit" size="small" variant="text" disableElevation>
                                        {t('general.alerts.unsaved_changes.buttons.yes')}
                                    </Button>
                                    <Button onClick={() => closeModal(true)} color="inherit" size="small" variant="text" disableElevation>
                                        {t('general.alerts.unsaved_changes.buttons.no')}
                                    </Button>
                                </>
                            }
                        >
                            <AlertTitle>{t('general.alerts.unsaved_changes.message')}</AlertTitle>
                        </Alert>
                    </Box>
                )}

                <DialogContent dividers sx={{ background: (theme) => theme.palette.background.default }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        {data?.full_name || ''}
                    </Typography>

                    <ContractTimeline rows={sortedRows} onBoundaryClick={handleTimelineBoundaryClick} onSegmentClick={handleTimelineSegmentClick} />

                    <Box sx={{ height: 420 }}>
                        <DataGrid
                            rows={sortedRows}
                            columns={columns}
                            getRowId={(row) => row.local_id}
                            hideFooter
                            disableRowSelectionOnClick
                            loading={loading || saving}
                            rowHeight={72}
                            sx={{
                                '& .MuiDataGrid-cell': {
                                    alignItems: 'center',
                                },
                            }}
                        />
                    </Box>
                </DialogContent>

                <DialogActions sx={{ background: (theme) => theme.palette.background.default }}>
                    <Button variant="contained" disableElevation color="error" onClick={() => closeModal(false)}>
                        {t('timekeeper.employees.dialog.assign_contract.buttons.cancel')}
                    </Button>

                    <Tooltip title={tooltipContent !== '' ? tooltipContent : ''}>
                        <span>
                            <Button variant="contained" disableElevation onClick={persistRows} loading={saving} disabled={loading || !!managerValidation}>
                                {t('general.buttons.save')}
                            </Button>
                        </span>
                    </Tooltip>
                </DialogActions>
            </Dialog>

            <ContractDetailModal
                open={!!editingRow}
                row={editingRow}
                onClose={() => setEditingRowId(null)}
                onSave={(payload) => {
                    updateRow(editingRow.local_id, payload);
                    setEditingRowId(null);
                }}
            />

            <Popover
                open={!!timelineEditor.anchorEl}
                anchorEl={timelineEditor.anchorEl}
                onClose={closeTimelineEditor}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Box sx={{ p: 1 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                        <DateCalendar value={selectedBoundaryDate} onChange={(value) => applyTimelineBoundaryDate(value)} />
                    </LocalizationProvider>
                </Box>
            </Popover>
        </>
    );
};

export { AssignContract };
